import React, { useEffect, useState } from 'react'
import { api, getApiErrorMessage } from '../api'
import { useI18n } from '../i18n'

// Contract controls only. These requests never send a message, charge a fan,
// simulate payment or switch manual mode off.
export default function PaidScriptSessionPanel({ modelId, fanId, manualMode }) {
  const { t } = useI18n()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [scriptId, setScriptId] = useState('')
  const [quoteId, setQuoteId] = useState('')
  const [price, setPrice] = useState('')
  const base = `/bot/models/${modelId}/fans/${fanId}/paid-sessions`

  async function request(path, options) {
    const response = await api(path, options)
    const body = await response.json()
    if (!response.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Session request failed')
    return body
  }

  useEffect(() => {
    let live = true
    setData(null); setError(''); setExpanded(false); setScriptId(''); setQuoteId(''); setPrice('')
    request(base).then(value => { if (live) setData(value) }).catch(err => {
      if (live) setError(getApiErrorMessage(err, 'Could not load paid sessions'))
    })
    return () => { live = false }
  }, [base])

  const hasOpenSession = data?.sessions?.some(row => ['pending_payment', 'active', 'paused', 'delivery_review'].includes(row.status))
  useEffect(() => {
    if (!hasOpenSession) return undefined
    let live = true
    const timer = window.setInterval(() => {
      if (!document.hidden) request(base).then(value => { if (live) setData(value) }).catch(() => {})
    }, 15000)
    return () => { live = false; window.clearInterval(timer) }
  }, [base, hasOpenSession])

  async function act(path, method, payload) {
    setBusy(true); setError('')
    try {
      await request(path, { method, body: JSON.stringify(payload) })
      setData(await request(base)); setExpanded(false)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not update paid session'))
    } finally { setBusy(false) }
  }

  if (!data) return error ? <div role="alert">{error}</div> : null
  const session = data.sessions.find(row => ['pending_payment', 'active', 'paused', 'delivery_review'].includes(row.status))
  if (!data.enabled && !session) return null
  const money = session ? `${session.currency} ${(session.price_minor / 100).toFixed(2)}` : ''
  const eligibleScripts = data.scripts.filter(script => script.paid_sexting_config?.enabled === true)
  return <section className="v2-panel" style={{ padding: '12px', minWidth: 0 }}>
    <strong>Prepaid script session</strong>
    {error ? <p role="alert">{error}</p> : null}
    {session ? <>
      <p>{session.script_name} · {money} · {session.status.replaceAll('_', ' ')}</p>
      <p>{session.remaining_stages} stages left. Included media carry no extra charge.</p>
      {session.paid && manualMode ? <p>Payment verified. Manual mode stays ON until the operator enables auto replies.</p> : null}
      {!session.paid ? <p>Awaiting verified payment; registering this quote does not charge the fan.</p> : null}
      {!session.paid && data.enabled ? (data.payment_candidates || []).map(receipt => <button key={receipt.id} className="v2-btn v2-btn-secondary" disabled={busy} onClick={() => {
        if (window.confirm(`Have you checked receipt #${receipt.id} in the provider account and confirmed that it pays for this session? Manual mode will NOT change.`)) act(`${base}/${session.session_id}`, 'PATCH', { action: 'approve_payment', purchase_id: receipt.id, expected_version: session.version })
      }}>Confirm existing receipt #{receipt.id}: {receipt.currency} {receipt.amount}</button>) : null}
      {!data.enabled ? <p>Feature disabled: delivery is held; no fallback to another PPV.</p> : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        <button className="v2-btn v2-btn-ghost" disabled={busy || !session.paid || !['active', 'delivery_review'].includes(session.status)} onClick={() => act(`${base}/${session.session_id}`, 'PATCH', { action: 'pause', expected_version: session.version })}>Pause session</button>
        <button className="v2-btn v2-btn-ghost" disabled={busy || !session.paid || !data.enabled || session.status !== 'paused'} onClick={() => act(`${base}/${session.session_id}`, 'PATCH', { action: 'resume', expected_version: session.version })}>Resume session</button>
        <button className="v2-btn v2-btn-ghost" disabled={busy} onClick={() => {
          if (window.confirm('Cancel this session? This does not refund a payment or remove acquired access.')) act(`${base}/${session.session_id}`, 'PATCH', { action: 'cancel', expected_version: session.version })
        }}>Cancel session</button>
      </div>
      {session.status === 'delivery_review' ? <p>Review the unavailable material or delivery result before resuming; no automatic resend.</p> : null}
    </> : <>
      <p>One operator-approved price for the remaining script. Media are delivered gradually, not as new PPV offers.</p>
      {!eligibleScripts.length ? <p>{t('paidScript.noScripts')}</p> : null}
      <button className="v2-btn v2-btn-ghost" disabled={!eligibleScripts.length} onClick={() => setExpanded(!expanded)}>Register an existing quote</button>
      {expanded ? <form style={{ display: 'grid', gap: '8px', marginTop: '8px' }} onSubmit={event => {
        event.preventDefault()
        if (!/^\d+(\.\d{1,2})?$/.test(price)) { setError('Enter a USD amount with up to two decimal places'); return }
        const [whole, fraction = ''] = price.split('.')
        const minor = Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
        act(base, 'POST', { script_id: Number(scriptId), quote_message_id: Number(quoteId), price_minor: minor, payment_kind: 'tip', currency: 'USD' })
      }}>
        <label>Script<select required value={scriptId} onChange={event => {
          setScriptId(event.target.value)
          const script = eligibleScripts.find(row => String(row.id) === event.target.value)
          setPrice(script ? (script.paid_sexting_config.price_minor / 100).toFixed(2) : '')
        }} style={{ width: '100%', minWidth: 0 }}><option value="">Select script</option>{eligibleScripts.map(script => <option key={script.id} value={script.id}>{script.name} · USD {(script.paid_sexting_config.price_minor / 100).toFixed(2)}</option>)}</select></label>
        <label>Delivered quote message ID<input required type="number" min="1" value={quoteId} onChange={event => setQuoteId(event.target.value)} style={{ width: '100%' }} /></label>
        <label>Quoted total, USD<input required inputMode="decimal" readOnly value={price} style={{ width: '100%' }} /></label>
        <small>The total must match both the script tariff and the delivered quote. Change future prices in Content. Verified tip required. This does not change manual mode.</small>
        <button className="v2-btn v2-btn-secondary" disabled={busy}>Save quote only</button>
      </form> : null}
    </>}
  </section>
}
